import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react'
import { ActionIcon, Badge, Box, Button, Group, Menu, ScrollArea, Stack, Text, TextInput, Tooltip, UnstyledButton } from '@mantine/core'
import { openConfirmModal } from '@mantine/modals'
import { IconExternalLink, IconPlus, IconX } from '@/components/icons'
import { ProjectIcon } from '@/components/sidebar/ProjectIcon'
import { formatDateTimeDot, formatRelativeAgoRu, statusLabel } from '@/lib/ticket-display'
import { useTheme } from '@/lib/theme-context'
import { normalizeTagColorHex, ticketTagChipStyle } from '@/lib/ticket-tag-ui'
import type { ProjectRow, ProjectTagRow, TicketListItem } from '@/types/api'

/** Метки в одной строке с «Переписка»; не влезающие — «+N», при наведении имена скрытых. */
export function TicketChatHeaderTagsRow({
  ticketTagIds,
  tags,
  tagsToAdd,
  onRemoveTag,
  onAddTag,
}: {
  ticketTagIds: string[]
  tags: ProjectTagRow[]
  tagsToAdd: ProjectTagRow[]
  onRemoveTag: (tagId: string) => void
  onAddTag: (tagId: string) => void
}) {
  const orderedTags = useMemo(
    () =>
      ticketTagIds
        .map((id) => tags.find((t) => t.id === id))
        .filter((t): t is ProjectTagRow => Boolean(t)),
    [ticketTagIds, tags],
  )

  const showAdd = tagsToAdd.length > 0
  const widthSlotRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const [visibleCount, setVisibleCount] = useState(() => orderedTags.length)
  const [addMenuOpened, setAddMenuOpened] = useState(false)
  const [tagSearch, setTagSearch] = useState('')

  const filteredTagsToAdd = useMemo(() => {
    const q = tagSearch.trim().toLowerCase()
    if (!q) return tagsToAdd
    return tagsToAdd.filter((tag) => {
      const name = (tag.name || '').toLowerCase()
      const id = (tag.id || '').toLowerCase()
      return name.includes(q) || id.includes(q)
    })
  }, [tagSearch, tagsToAdd])

  const recomputeVisible = useCallback(() => {
    const slot = widthSlotRef.current
    const measureEl = measureRef.current
    if (!slot) return
    const W = slot.clientWidth
    const n = orderedTags.length
    if (W <= 0 || n === 0) {
      setVisibleCount(n)
      return
    }

    const chipEls = measureEl?.querySelectorAll<HTMLElement>('[data-measure-tag-chip]')
    const widths =
      chipEls && chipEls.length === n
        ? [...chipEls].map((el) => el.getBoundingClientRect().width)
        : orderedTags.map(() => 88)

    const moreEl = measureEl?.querySelector<HTMLElement>('[data-measure-more]')
    const addEl = measureEl?.querySelector<HTMLElement>('[data-measure-add]')
    const moreW = moreEl?.getBoundingClientRect().width ?? 40
    const addW = showAdd && addEl ? addEl.getBoundingClientRect().width : 0

    const gs = 6
    let best = n
    for (let v = n; v >= 0; v--) {
      const hidden = n - v
      let used = 0
      for (let j = 0; j < v; j++) {
        used += (widths[j] ?? 72) + (j > 0 ? gs : 0)
      }
      if (hidden > 0) used += (v > 0 ? gs : 0) + moreW
      if (showAdd && addW > 0) used += (v > 0 || hidden > 0 ? gs : 0) + addW
      if (used <= W) {
        best = v
        break
      }
    }
    setVisibleCount(best)
  }, [orderedTags, showAdd])

  useLayoutEffect(() => {
    recomputeVisible()
  }, [recomputeVisible])

  useEffect(() => {
    const el = widthSlotRef.current
    if (!el) return
    const ro = new ResizeObserver(() => recomputeVisible())
    ro.observe(el)
    return () => ro.disconnect()
  }, [recomputeVisible])

  const visibleTags = orderedTags.slice(0, visibleCount)
  const hiddenTags = orderedTags.slice(visibleCount)
  const hiddenLabel =
    hiddenTags.length > 0 ? hiddenTags.map((t) => `«${t.name}»`).join(', ') : ''

  const renderChipInner = (tag: ProjectTagRow) => {
    const hex = normalizeTagColorHex(tag.color)
    return (
      <>
        <Box
          style={{
            width: 5,
            height: 5,
            borderRadius: 999,
            backgroundColor: hex,
            flexShrink: 0,
          }}
        />
        <Text component="span" fw={500} truncate style={{ minWidth: 0, fontSize: 11, lineHeight: 1.2 }}>
          {tag.name}
        </Text>
        <IconX size={11} stroke={1.5} style={{ flexShrink: 0, opacity: 0.55 }} />
      </>
    )
  }

  return (
    <Box
      ref={widthSlotRef}
      style={{
        flex: 1,
        minWidth: 0,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
      }}
    >
      <Box
        ref={measureRef}
        aria-hidden
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          visibility: 'hidden',
          pointerEvents: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          whiteSpace: 'nowrap',
          zIndex: -1,
        }}
      >
        {orderedTags.map((tag) => (
          <Box
            key={tag.id}
            data-measure-tag-chip
            style={{ ...ticketTagChipStyle(normalizeTagColorHex(tag.color)), maxWidth: 176 }}
          >
            {renderChipInner(tag)}
          </Box>
        ))}
        {orderedTags.length > 0 ? (
          <Badge data-measure-more size="xs" variant="light" color="gray" style={{ flexShrink: 0 }}>
            +{orderedTags.length}
          </Badge>
        ) : null}
        {showAdd ? (
          <Button
            data-measure-add
            size="compact-xs"
            variant="default"
            leftSection={<IconPlus size={12} stroke={1.5} />}
            px={6}
            py={2}
            fz={11}
            h="auto"
            lh={1.2}
            style={{ flexShrink: 0 }}
          >
            Добавить метку
          </Button>
        ) : null}
      </Box>

      <Group wrap="nowrap" gap={6} align="center" style={{ minWidth: 0, overflow: 'hidden', width: '100%' }}>
        {visibleTags.map((tag) => {
          const hex = normalizeTagColorHex(tag.color)
          const tid = tag.id
          return (
            <UnstyledButton
              key={tid}
              type="button"
              className="ticket-tag-chip"
              title="Удалить метку"
              aria-label={`Метка ${tag.name}, удалить`}
              onClick={() =>
                openConfirmModal({
                  title: 'Снять метку?',
                  children: (
                    <Text size="sm" c="dimmed">
                      Метка «{tag.name}» будет убрана с этого обращения.
                    </Text>
                  ),
                  labels: { confirm: 'Снять', cancel: 'Отмена' },
                  confirmProps: { color: 'red' },
                  onConfirm: () => void onRemoveTag(tid),
                })
              }
              style={{ ...ticketTagChipStyle(hex), flexShrink: 0 }}
            >
              {renderChipInner(tag)}
            </UnstyledButton>
          )
        })}
        {hiddenTags.length > 0 ? (
          <Tooltip label={hiddenLabel} multiline maw={320} withArrow position="bottom">
            <Badge size="xs" variant="light" color="gray" style={{ flexShrink: 0, cursor: 'default' }}>
              +{hiddenTags.length}
            </Badge>
          </Tooltip>
        ) : null}
        {showAdd ? (
          <Menu
            shadow="md"
            width={320}
            position="bottom-start"
            withinPortal
            opened={addMenuOpened}
            onChange={(opened) => {
              setAddMenuOpened(opened)
              if (!opened) setTagSearch('')
            }}
          >
            <Menu.Target>
              <Button
                size="compact-xs"
                variant="default"
                className="ticket-tag-add-btn"
                leftSection={<IconPlus size={12} stroke={1.5} />}
                px={6}
                py={2}
                fz={11}
                h="auto"
                lh={1.2}
                style={{ flexShrink: 0 }}
              >
                Добавить метку
              </Button>
            </Menu.Target>
            <Menu.Dropdown p={0}>
              <Menu.Label px="sm" pt="xs" pb={6}>
                Выберите метку
              </Menu.Label>
              <Box px="xs" pb="xs">
                <TextInput
                  size="xs"
                  value={tagSearch}
                  onChange={(e) => setTagSearch(e.currentTarget.value)}
                  placeholder="Найти метку по названию"
                  aria-label="Поиск метки по названию"
                  autoFocus
                />
              </Box>
              <ScrollArea.Autosize
                type="auto"
                scrollbarSize={8}
                offsetScrollbars="y"
                styles={{
                  root: {
                    maxHeight: 'min(58vh, 400px)',
                  },
                }}
              >
                {filteredTagsToAdd.length === 0 ? (
                  <Text size="xs" c="dimmed" px="sm" py="xs">
                    Метки не найдены
                  </Text>
                ) : null}
                {filteredTagsToAdd.map((t) => {
                  const hx = normalizeTagColorHex(t.color)
                  return (
                    <Menu.Item
                      key={t.id}
                      onClick={() =>
                        openConfirmModal({
                          title: 'Добавить метку?',
                          children: (
                            <Text size="sm" c="dimmed">
                              Метка «{t.name}» будет добавлена к обращению.
                            </Text>
                          ),
                          labels: { confirm: 'Добавить', cancel: 'Отмена' },
                          onConfirm: () => void onAddTag(t.id),
                        })
                      }
                      leftSection={
                        <Box
                          style={{
                            width: 11,
                            height: 11,
                            borderRadius: 999,
                            backgroundColor: hx,
                            border: '1px solid var(--mantine-color-default-border)',
                            flexShrink: 0,
                          }}
                        />
                      }
                    >
                      {t.name}
                    </Menu.Item>
                  )
                })}
              </ScrollArea.Autosize>
            </Menu.Dropdown>
          </Menu>
        ) : null}
      </Group>
    </Box>
  )
}

/** Фон строки прошлого обращения по статусу (по требованию заказчика).
 *  Новый/Открыто/В работе — без цвета; в тёмной теме — мягкий полупрозрачный оттенок. */
function previousTicketRowBackground(
  status: string | null | undefined,
  isDark: boolean,
): string | undefined {
  const light: Record<string, string> = {
    emergency: '#fae8e6', // Авария — красноватый
    resolved: '#e4f8f1', // Решён — зелёный
    closed: '#f5f6f8', // Закрыт — серый
    pending: '#e2e4e9', // Ожидание — другой серый
  }
  const s = String(status ?? '')
  const base = light[s]
  if (!base) return undefined
  if (!isDark) return base
  // В тёмной теме используем лёгкий оттенок, чтобы светлый текст оставался читаемым.
  const darkMix: Record<string, string> = {
    emergency: 'color-mix(in srgb, #fae8e6 14%, transparent)',
    resolved: 'color-mix(in srgb, #e4f8f1 14%, transparent)',
    closed: 'color-mix(in srgb, #f5f6f8 9%, transparent)',
    pending: 'color-mix(in srgb, #e2e4e9 11%, transparent)',
  }
  return darkMix[s]
}

export type PreviousTicketsRowsListProps = {
  items: TicketListItem[]
  projectPick: (
    projectId: string,
    fallbackName: string | null | undefined,
  ) => Pick<ProjectRow, 'name' | 'color' | 'icon' | 'icon_url'>
  onSelectTicket: (pt: TicketListItem) => void
  onOpenInNewTab: (pt: TicketListItem, e: ReactMouseEvent) => void
  /** id обращений, открытых в рабочих вкладках ('active' — активная вкладка). */
  tabStateById?: Map<string, 'active' | 'open'>
}

export function PreviousTicketsRowsList({
  items,
  projectPick,
  onSelectTicket,
  onOpenInNewTab,
  tabStateById,
}: PreviousTicketsRowsListProps) {
  const { theme: appTheme } = useTheme()
  const isDark = appTheme === 'dark'
  return (
    <Stack gap={6}>
      {items.map((pt) => {
        // Дата и время СОЗДАНИЯ обращения (как давно клиент обращался), а не последнего сообщения.
        const whenIso = pt.created_at || pt.updated_at || pt.last_update_at
        const rel = formatRelativeAgoRu(whenIso)
        const iconProject = projectPick(pt.project_id, pt.project_name)
        const rowBg = previousTicketRowBackground(pt.status, isDark)
        const tabState = tabStateById?.get(pt.id)
        const borderColor = tabState
          ? 'var(--mantine-primary-color-filled)'
          : 'transparent'
        return (
          <Box
            key={pt.id}
            tabIndex={0}
            role="button"
            title={tabState ? 'Открыто в рабочей вкладке' : undefined}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onSelectTicket(pt)
              }
            }}
            onClick={() => onSelectTicket(pt)}
            p={6}
            style={{
              borderRadius: 'var(--mantine-radius-sm)',
              cursor: 'pointer',
              outline: 'none',
              background: rowBg,
              // Прозрачная рамка у всех строк, чтобы выделение открытых вкладок не сдвигало вёрстку.
              border: `${tabState === 'active' ? 2 : 1}px solid ${borderColor}`,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--mantine-color-default-hover)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = rowBg ?? 'transparent'
            }}
          >
            <Group gap={8} wrap="nowrap" align="flex-start" style={{ width: '100%' }}>
              <ProjectIcon project={iconProject} boxSize={18} />
              <Stack gap={4} align="stretch" style={{ flex: 1, minWidth: 0 }}>
                <Group gap={6} wrap="nowrap" align="flex-start">
                  <Text
                    size="xs"
                    lineClamp={3}
                    style={{ flex: 1, minWidth: 0, whiteSpace: 'pre-wrap' }}
                  >
                    {pt.subject || '—'}
                  </Text>
                  <Tooltip label="Открыть в новой вкладке браузера">
                    <ActionIcon
                      variant="subtle"
                      size="sm"
                      aria-label="Открыть в новой вкладке браузера"
                      style={{ flexShrink: 0 }}
                      onClick={(e) => onOpenInNewTab(pt, e)}
                    >
                      <IconExternalLink size={16} stroke={1.5} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
                <Text size="xs" c="dimmed">
                  {formatDateTimeDot(whenIso)}
                  {rel ? ` · ${rel}` : ''} • {statusLabel(pt.status)}
                </Text>
              </Stack>
            </Group>
          </Box>
        )
      })}
    </Stack>
  )
}
